// pages/Login.js - Updated with proper copyright and no demo credentials
import { useState } from "react";
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from "firebase/auth";
import { auth, database } from "../firebase/firebase";
import { ref, get } from "firebase/database";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const loginUser = async () => {
    if (!username || !password) {
      setError("Please enter username and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const email = username + "@jauhari.webtech";

      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      const snapshot = await get(ref(database, "users/" + uid));

      if (!snapshot.exists()) {
        setError("User role not configured in database. Please contact administrator.");
        setLoading(false);
        return;
      }

      const userData = snapshot.val();

      if (userData.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error(err);
      if (err.code === "auth/user-not-found") {
        setError("User not found. Please check your username.");
      } else if (err.code === "auth/wrong-password") {
        setError("Incorrect password. Please try again.");
      } else {
        setError("Authentication failed. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      loginUser();
    }
  };

  return (
    <div className="login-page">
      {/* Background Animation */}
      <div className="bg-animation">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>

      <div className="login-container">
        {/* Logo Section */}
        <div className="logo-section">
          <div className="logo-icon">📊</div>
          <h1 className="logo-text">IndianTrade Billing</h1>
          <p className="logo-subtitle">Complete Business Management Solution</p>
        </div>

        {/* Login Card */}
        <div className="login-card">
          <div className="card-header">
            <h2>Welcome Back</h2>
            <p>Sign in to your account</p>
          </div>

          <div className="card-body">
            <div className="input-field">
              <label>Username</label>
              <div className="input-wrapper">
                <span className="input-icon">👤</span>
                <input
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="input-field">
              <label>Password</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="error-alert">
                <span className="error-icon">⚠️</span>
                <span className="error-text">{error}</span>
              </div>
            )}

            <button 
              className="login-button" 
              onClick={loginUser}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p>© 2026 Jauhari WebTech. All rights reserved.</p>
          <p className="footer-note">Powered by Jauhari WebTech </p>
        </div>
      </div>

      <style>{`
        /* Reset and Base Styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .login-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow-x: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }

        /* Animated Background Shapes */
        .bg-animation {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: 0;
        }

        .bg-shape {
          position: absolute;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          animation: float 20s infinite ease-in-out;
        }

        .shape-1 {
          width: 300px;
          height: 300px;
          top: -150px;
          right: -150px;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 200px;
          height: 200px;
          bottom: -100px;
          left: -100px;
          animation-delay: 5s;
        }

        .shape-3 {
          width: 150px;
          height: 150px;
          bottom: 30%;
          right: 10%;
          animation-delay: 10s;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(10deg);
          }
        }

        /* Main Container */
        .login-container {
          position: relative;
          z-index: 1;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        /* Logo Section */
        .logo-section {
          text-align: center;
          margin-bottom: 40px;
          animation: fadeInDown 0.6s ease;
        }

        .logo-icon {
          font-size: 60px;
          margin-bottom: 15px;
          display: inline-block;
          animation: gentlePulse 2s infinite ease-in-out;
        }

        @keyframes gentlePulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .logo-text {
          font-size: 28px;
          font-weight: 700;
          color: white;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .logo-subtitle {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
        }

        /* Login Card */
        .login-card {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 440px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: fadeInUp 0.6s ease;
          overflow: hidden;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .card-header {
          padding: 32px 32px 0 32px;
          text-align: center;
        }

        .card-header h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 8px;
        }

        .card-header p {
          font-size: 14px;
          color: #6b7280;
        }

        .card-body {
          padding: 32px;
        }

        /* Input Fields */
        .input-field {
          margin-bottom: 24px;
        }

        .input-field label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          font-size: 18px;
          color: #9ca3af;
          pointer-events: none;
        }

        .input-wrapper input {
          width: 100%;
          padding: 14px 14px 14px 44px;
          border: 1.5px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.2s;
          background: white;
          font-family: inherit;
        }

        .input-wrapper input:focus {
          outline: none;
          border-color: #5a60ea;
          box-shadow: 0 0 0 3px rgba(90, 96, 234, 0.1);
        }

        .input-wrapper input:disabled {
          background: #f9fafb;
          cursor: not-allowed;
        }

        /* Error Alert */
        .error-alert {
          background: #fef2f2;
          border-left: 3px solid #ef4444;
          padding: 12px 16px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 24px;
          animation: shake 0.4s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .error-icon {
          font-size: 16px;
        }

        .error-text {
          font-size: 13px;
          color: #dc2626;
          flex: 1;
        }

        /* Login Button */
        .login-button {
          width: 100%;
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          border: none;
          padding: 14px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-family: inherit;
        }

        .login-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(90, 96, 234, 0.3);
        }

        .login-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Footer */
        .login-footer {
          margin-top: 40px;
          text-align: center;
        }

        .login-footer p {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 4px;
        }

        .footer-note {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.6);
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .login-container {
            padding: 16px;
            justify-content: flex-start;
            padding-top: 60px;
          }

          .logo-section {
            margin-bottom: 30px;
          }

          .logo-icon {
            font-size: 48px;
          }

          .logo-text {
            font-size: 24px;
          }

          .logo-subtitle {
            font-size: 12px;
          }

          .login-card {
            max-width: 100%;
            border-radius: 20px;
          }

          .card-header {
            padding: 24px 24px 0 24px;
          }

          .card-header h2 {
            font-size: 24px;
          }

          .card-body {
            padding: 24px;
          }

          .input-field {
            margin-bottom: 20px;
          }

          .input-wrapper input {
            padding: 12px 12px 12px 40px;
            font-size: 14px;
          }

          .input-icon {
            left: 12px;
            font-size: 16px;
          }

          .login-button {
            padding: 12px;
            font-size: 15px;
          }

          .login-footer {
            margin-top: 30px;
          }
        }

        /* Small Mobile Styles */
        @media (max-width: 480px) {
          .login-container {
            padding: 12px;
            padding-top: 40px;
          }

          .logo-icon {
            font-size: 40px;
          }

          .logo-text {
            font-size: 20px;
          }

          .card-header h2 {
            font-size: 22px;
          }

          .card-body {
            padding: 20px;
          }

          .input-field label {
            font-size: 13px;
          }

          .input-wrapper input {
            padding: 10px 10px 10px 38px;
            font-size: 13px;
          }
        }

        /* Tablet Styles */
        @media (min-width: 769px) and (max-width: 1024px) {
          .login-card {
            max-width: 420px;
          }

          .logo-text {
            font-size: 32px;
          }
        }

        /* Desktop Styles */
        @media (min-width: 1025px) {
          .login-card {
            max-width: 480px;
          }

          .login-button {
            padding: 16px;
            font-size: 17px;
          }
        }

        /* Loading State */
        .input-wrapper input:disabled {
          background: #f9fafb;
        }

        /* Focus Visible for Accessibility */
        .login-button:focus-visible,
        .input-wrapper input:focus-visible {
          outline: 2px solid #5a60ea;
          outline-offset: 2px;
        }

        /* Reduced Motion Preference */
        @media (prefers-reduced-motion: reduce) {
          .bg-shape,
          .logo-icon,
          .login-card,
          .error-alert {
            animation: none;
          }
          
          .login-button:hover:not(:disabled) {
            transform: none;
          }
        }
      `}</style>
    </div>
  );
}